import type { InboundMessageResponse, MockSessionResponse, SeedHandle, WorldSpec } from './types';

const API_BASE = '/v1';

export async function requestMockSession(
  tenantId: string,
  label: string,
): Promise<MockSessionResponse> {
  const res = await fetch(`${API_BASE}/_mock/simulador-session-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tenantId, displayLabel: label }),
  });
  if (!res.ok) {
    throw new Error(`Falha ao obter session token: HTTP ${String(res.status)}`);
  }
  return res.json() as Promise<MockSessionResponse>;
}

export async function sendInboundMessage(input: {
  tenantId: string;
  channel: string;
  channelIdentifier: string;
  content: string;
  displayName?: string;
}): Promise<InboundMessageResponse> {
  const res = await fetch(`${API_BASE}/_mock/inbound-message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`Falha ao enviar inbound: HTTP ${String(res.status)}`);
  }
  return res.json() as Promise<InboundMessageResponse>;
}

/**
 * Semeia o MUNDO declarado e devolve o `channelIdentifier` com que conversar.
 *
 * É o que separa este simulador de uma janela de chat: quem vai testar **prepara o cenário**
 * antes de falar. Sem isto, o único mundo disponível era o que as fixtures deixaram no banco —
 * e ele era incoerente (`verified: true` com proveniência `observed`, medido em 19/Ago/2026).
 *
 * Dev-only: o endpoint é bloqueado em produção pelo próprio controller (`assertDevOnly`), e o
 * painel que o chama só é montado sob `import.meta.env.DEV`.
 */
export async function seedWorld(world: WorldSpec): Promise<SeedHandle> {
  const res = await fetch(`${API_BASE}/_mock/seed-world`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(world),
  });
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    // A mensagem do engine é o valor aqui: mundo contraditório volta explicando POR QUE, e
    // engolir isso num «HTTP 400» genérico devolveria o problema ao operador sem a razão.
    const detail =
      typeof body === 'object' && body !== null && 'message' in body
        ? String(body.message)
        : `HTTP ${String(res.status)}`;
    throw new Error(detail);
  }
  return body as SeedHandle;
}

/**
 * Busca o que o CLIENTE veria na conversa — SEC-001.
 *
 * O broadcast de tenant do engine deixou de levar o `content` das mensagens (ele ia para
 * TODO operador conectado, furando o ACL que o REST aplica). Então o evento do WebSocket
 * virou AVISO e esta chamada é a ENTREGA — o mesmo padrão que o `lume-atendimento` e o
 * app móvel já usavam.
 *
 * ⚠️ Este endpoint FILTRA mensagem que falhou no envio. Isso é de propósito: mensagem
 * `failed` existe no banco e o cliente real nunca a recebeu, e este simulador existe para
 * sentar na cadeira do cliente.
 */
export async function fetchConversationMessages(input: {
  tenantId: string;
  conversationId: string;
}): Promise<{ data: { id: string; origin: string; content: string; sentAt: string }[] }> {
  const qs = new URLSearchParams({
    tenantId: input.tenantId,
    conversationId: input.conversationId,
  });
  const res = await fetch(`${API_BASE}/_mock/conversation-messages?${qs.toString()}`);
  if (!res.ok) {
    throw new Error(`Falha ao buscar mensagens: HTTP ${String(res.status)}`);
  }
  return res.json() as Promise<{
    data: { id: string; origin: string; content: string; sentAt: string }[];
  }>;
}

/**
 * Item 515 — a conversa mais recente DESTE cliente, segundo o engine (`conversation-audit`, que
 * resolve por `(tenantId, channelIdentifier)`). É a prova de posse antes de adotar a conversa de um
 * evento: o evento é do tenant inteiro e não diz de quem é. Falha ⇒ `null` (não adota).
 */
export async function fetchOwnConversationId(input: {
  tenantId: string;
  channelIdentifier: string;
}): Promise<string | null> {
  const qs = new URLSearchParams(input);
  try {
    const res = await fetch(`${API_BASE}/_mock/conversation-audit?${qs.toString()}`);
    if (!res.ok) return null;
    const body = (await res.json()) as {
      snapshot?: { conversation?: { conversationId?: string | null } };
    };
    return body.snapshot?.conversation?.conversationId ?? null;
  } catch {
    return null;
  }
}
